import React, { useState } from 'react';
import { Info } from 'lucide-react';
import { OwnerButton, OwnerShell } from '../OwnerShell';
import { FlowSteps } from '../FlowSteps';
import { UploadTile } from '../UploadTile';
import type { UploadState } from '../UploadTile';
import { toBn } from '../../../utils/bn';
import type { OwnerNav } from '../../../types/owner';
interface DocDef {
  key: string;
  label: string;
  required: boolean;
  optional?: boolean;
  number: string;
  issue: string;
  expiry: string;
  authority: string;
  initial: UploadState;
}
const cngDocs: DocDef[] = [{
  key: 'reg',
  label: 'রেজিস্ট্রেশন সার্টিফিকেট',
  required: true,
  number: 'BRTA-RC-DH-2022-118734',
  issue: '১২/০১/২০২২',
  expiry: 'প্রযোজ্য নয়',
  authority: 'BRTA, মিরপুর সার্কেল',
  initial: 'uploaded'
}, {
  key: 'fitness',
  label: 'ফিটনেস সার্টিফিকেট',
  required: true,
  number: 'FIT-2025-DH-558821',
  issue: '০১/০৭/২০২৫',
  expiry: '৩০/০৬/২০২৭',
  authority: 'BRTA, মিরপুর সার্কেল',
  initial: 'uploaded'
}, {
  key: 'tax',
  label: 'ট্যাক্স টোকেন',
  required: true,
  number: 'TAX-2025-0071120',
  issue: '০১/০১/২০২৫',
  expiry: '৩১/১২/২০২৫',
  authority: 'BRTA',
  initial: 'expired'
}, {
  key: 'route',
  label: 'রুট পারমিট',
  required: true,
  number: 'RP-DMP-CNG-2025-04417',
  issue: '১৬/১১/২০২৫',
  expiry: '১৫/১১/২০২৬',
  authority: 'মেট্রো RTC, ঢাকা',
  initial: 'uploaded'
}, {
  key: 'owner',
  label: 'মালিকানার প্রমাণপত্র',
  required: true,
  number: 'SALE-DEED-2021-7781',
  issue: '২৮/১২/২০২১',
  expiry: 'প্রযোজ্য নয়',
  authority: 'BRTA',
  initial: 'missing'
}, {
  key: 'photo',
  label: 'গাড়ির ছবি',
  required: true,
  number: 'সামনে, পেছনে ও দুই পাশ',
  issue: '১৩/১০/২০২৬',
  expiry: 'প্রযোজ্য নয়',
  authority: 'মালিক',
  initial: 'missing'
}];
const mclDocs: DocDef[] = [{
  key: 'reg',
  label: 'রেজিস্ট্রেশন সার্টিফিকেট',
  required: true,
  number: 'BRTA-RC-DM-2023-445678',
  issue: '০৮/০৩/২০২৩',
  expiry: 'প্রযোজ্য নয়',
  authority: 'BRTA, ইকুরিয়া',
  initial: 'uploaded'
}, {
  key: 'tax',
  label: 'ট্যাক্স টোকেন',
  required: true,
  number: 'TAX-2026-0211873',
  issue: '২১/০২/২০২৬',
  expiry: '২০/০২/২০২৭',
  authority: 'BRTA',
  initial: 'missing'
}, {
  key: 'owner',
  label: 'মালিকানার প্রমাণপত্র',
  required: true,
  number: 'INV-YMH-2023-00981',
  issue: '০২/০৩/২০২৩',
  expiry: 'প্রযোজ্য নয়',
  authority: 'ACI মোটরস',
  initial: 'uploaded'
}, {
  key: 'insurance',
  label: 'বীমা সনদ',
  required: false,
  optional: true,
  number: 'INS-GRN-2025-118745',
  issue: '০৬/১০/২০২৫',
  expiry: '০৫/১০/২০২৬',
  authority: 'গ্রিন ডেল্টা ইন্স্যুরেন্স',
  initial: 'expired'
}, {
  key: 'photo',
  label: 'গাড়ির ছবি',
  required: true,
  number: 'সামনে, পেছনে ও এক পাশ',
  issue: '১৩/১০/২০২৬',
  expiry: 'প্রযোজ্য নয়',
  authority: 'মালিক',
  initial: 'missing'
}];
export function OwnerDocuments({
  nav
}: {
  nav: OwnerNav;
}) {
  const cng = nav.draft.type === 'cng';
  const defs = cng ? cngDocs : mclDocs;
  const [state, setState] = useState<Record<string, UploadState>>(() => Object.fromEntries(defs.map(d => [d.key, d.initial])));
  const [renewed, setRenewed] = useState<Record<string, boolean>>({});
  const uploaded = defs.filter(d => state[d.key] === 'uploaded').length;
  const blocking = defs.filter(d => d.required && state[d.key] !== 'uploaded');
  const upload = (key: string, src: string) => {
    setState(s => ({
      ...s,
      [key]: 'uploaded'
    }));
    setRenewed(r => ({
      ...r,
      [key]: true
    }));
    nav.showToast(src === 'pdf' ? 'PDF আপলোড সম্পন্ন' : 'ছবি আপলোড সম্পন্ন');
  };
  const next = () => {
    if (blocking.length) return nav.showToast(`${toBn(blocking.length)}টি আবশ্যক ডকুমেন্ট বাকি বা মেয়াদোত্তীর্ণ`);
    nav.setDraft({
      ...nav.draft,
      docs: uploaded
    });
    nav.go(cng ? 'brta' : 'selfVerify');
  };
  return <OwnerShell nav={nav} title="গাড়ির প্রয়োজনীয় কাগজপত্র" subtitle={nav.draft.reg} bodyClassName="p-0" footer={<div className="grid grid-cols-2 gap-2" data-id="element-1501" data-mp="3dincz5dl9oae">
          <OwnerButton variant="outline" onClick={nav.back} data-id="element-1502" data-mp="3dhc975k58gja">পূর্ববর্তী</OwnerButton>
          <OwnerButton onClick={next} data-id="element-1503" data-mp="38irajakji4wy">পরবর্তী</OwnerButton>
        </div>} data-id="element-1500" data-mp="225bhbhrzgbzb">
      <FlowSteps current={2} data-id="element-1504" data-mp="1i3gvgqmx74ns" />
      <div className="space-y-2.5 px-4 py-4" data-id="element-1505" data-mp="1i49tn37tclub">
        <div className="flex items-center justify-between rounded-lg bg-white px-3 py-2 text-xs shadow-sm" data-id="element-1506" data-mp="1n1iejbkcj3z9">
          <span className="text-slate-500" data-id="element-1507" data-mp="1r2vljoz6dd7r">আপলোড সম্পন্ন</span>
          <span className="font-semibold text-navy-900" data-id="element-1508" data-mp="1r5htvqtw3wr1">{toBn(uploaded)}/{toBn(defs.length)}</span>
          <div className="ml-3 h-1.5 flex-1 overflow-hidden rounded-full bg-slate-200" data-id="element-1509" data-mp="1w0dlipv7si3m">
            <span className="block h-full bg-bdgreen-600 transition-all" style={{
            width: `${uploaded / defs.length * 100}%`
          }} data-id="element-1510" data-mp="211b24y0kt430" />
          </div>
        </div>
        {!cng && <p className="flex gap-2 rounded-lg bg-blue-50 p-2.5 text-[11.5px] text-blue-800" data-id="element-1511" data-mp="2asnsfl6q7m6x">
            <Info className="mt-0.5 h-4 w-4 shrink-0" data-id="element-1512" data-mp="2ero8wx1qecqr" />মোটরসাইকেলের জন্য ফিটনেস সার্টিফিকেট ও রুট পারমিট প্রযোজ্য নয়।
          </p>}
        {defs.map(d => <UploadTile key={d.key} label={d.label} required={d.required} optional={d.optional} state={state[d.key]} fileName={`${d.key}_${toBn(2026)}.jpg`} onUpload={src => upload(d.key, src)} data-id="element-1513" data-mp="2oqm58m5efakb">
            {state[d.key] !== 'missing' && <dl className="mt-2.5 grid grid-cols-2 gap-x-3 gap-y-1.5 rounded-lg bg-slate-50 p-2.5 text-[11px]" data-id="element-1514" data-mp="2tueli5gl32ia">
                <div data-id="element-1515" data-mp="2oyz8ffqehamx"><dt className="text-slate-400" data-id="element-1516" data-mp="2oyz9lj24zgoi">ডকুমেন্ট নম্বর</dt><dd className="truncate font-mono font-medium text-navy-900" data-id="element-1517" data-mp="2kw2y0cb0kku2">{d.number}</dd></div>
                <div data-id="element-1518" data-mp="3hltqk5zqvxx8"><dt className="text-slate-400" data-id="element-1519" data-mp="3hkim03yh6m52">ইস্যুর তারিখ</dt><dd className="font-medium text-navy-900" data-id="element-1520" data-mp="3hnoezua86862">{renewed[d.key] && d.initial === 'expired' ? '০১/১০/২০২৬' : d.issue}</dd></div>
                <div data-id="element-1521" data-mp="37v8rkhg5srds"><dt className="text-slate-400" data-id="element-1522" data-mp="37uz3izeocuaq">মেয়াদ শেষের তারিখ</dt><dd className={`font-medium ${state[d.key] === 'expired' ? 'text-red-600' : 'text-navy-900'}`} data-id="element-1523" data-mp="37tnykw9htgi1">{renewed[d.key] && d.initial === 'expired' ? '৩০/০৯/২০২৭' : d.expiry}</dd></div>
                <div data-id="element-1524" data-mp="47ytpdzdh18e"><dt className="text-slate-400" data-id="element-1525" data-mp="48s02duf5oq0">ইস্যুকারী কর্তৃপক্ষ</dt><dd className="font-medium text-navy-900" data-id="element-1526" data-mp="3vl393cbfb44d">{d.authority}</dd></div>
              </dl>}
          </UploadTile>)}
        <button onClick={() => setState(Object.fromEntries(defs.map(d => [d.key, 'uploaded'])))} className="w-full py-2 text-center text-[11px] font-medium text-amber-700" data-id="element-1527" data-mp="1tpvx3zz457se">
          প্রোটোটাইপ: সব ডকুমেন্ট একসাথে আপলোড করুন
        </button>
      </div>
    </OwnerShell>;
}